import {
  Component,
  OnInit,
  EventEmitter,
  Output,
} from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MaskPipe } from 'ngx-mask';
import Utils from 'src/app/helpers/utils';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { FundService } from 'src/app/services/fund/fund.service';
import { ToastService } from 'src/app/services/toast/toast.service';

@Component({
  selector: 'app-dashboard-fund-approval-shareholder',
  templateUrl: './dashboard-fund-approval-shareholder.component.html',
  styleUrls: ['./dashboard-fund-approval-shareholder.component.scss'],
})
export class DashboardFundApprovalShareholderComponent implements OnInit {
  @Output() onClick = new EventEmitter<MouseEvent>();

  loading = true;
  exporting = false;
  /** Lista exibida na tabela (vinda da API, com ou sem filtros). */
  data: any[] = [];

  canView = false;
  showExportOptions = false;
  showFilterModal = false;
  hasFilters = false;
  appliedFilters: Array<{ label: string; value: string }> = [];
  filterForm: FormGroup;
  get canApplyFilters(): boolean {
    const v = this.filterForm?.value ?? {};
    if (v?.name?.trim() || v?.document?.trim()) return true;
    const hasStatus =
      v?.statusHomologado ||
      v?.statusEmAprovacao ||
      v?.statusRejeitado ||
      v?.statusEmEdicao;
    const hasProfile =
      v?.profileConservador || v?.profileModerado || v?.profileAgressivo;
    const hasInclude = v?.exportIncludeEmail || v?.exportIncludePhone;
    return !!(hasStatus || hasProfile || hasInclude);
  }

  status_dict: Record<string, string> = {
    created: 'Em edição',
    INITIATED: 'Iniciado',
    AVAIABLE: 'Disponível',
    REQUESTED: 'Solicitado',
    APPROVED: 'Aprovado',
    REJECTED: 'Rejeitado',
  };

  /** Labels por approval_status (valores que a API devolve). */
  private approvalStatusLabel: Record<string, string> = {
    aprovado: 'Homologado',
    em_aprovacao: 'Em aprovação',
    rejeitado: 'Rejeitado',
    em_edicao: 'Em edição',
  };

  /** Quando a API envia approval_status, usa esse mapeamento; senão fallback para status antigo. */
  getShareholderStatusLabel(row: any): string {
    const reg = row?.links?.register;
    if (!reg) return '-';
    if (reg.approval_status && this.approvalStatusLabel[reg.approval_status])
      return this.approvalStatusLabel[reg.approval_status];
    if (reg.approval_status === 'aprovado') return 'Homologado';
    return this.status_dict[reg.status] ?? reg.status ?? '-';
  }

  /** Classe do badge de status (igual outras listagens). */
  getShareholderStatusBadgeClass(row: any): string {
    const reg = row?.links?.register;
    if (!reg) return 'status-badge-editing';
    if (reg.approval_status === 'aprovado') return 'status-badge-homologated';
    if (reg.approval_status === 'rejeitado') return 'status-badge-rejected';
    if (reg.approval_status === 'em_aprovacao') return 'status-badge-approval';
    if (reg.approval_status === 'em_edicao') return 'status-badge-editing';
    const s = (reg.status || '').toUpperCase();
    if (s === 'REJECTED') return 'status-badge-rejected';
    if (s === 'APPROVED' || s === 'REQUESTED' || s === 'INITIATED')
      return 'status-badge-approval';
    return 'status-badge-editing';
  }

  /** Dados do perfil de investidor para exibição (Perfil/Validade). API: register.investor_profile. */
  getShareholderInvestorProfileData(
    row: any,
  ): {
    profile: number | null;
    not_declared: boolean;
    days_until_expiration: number | null;
    expired: boolean;
  } | null {
    const reg = row?.links?.register;
    if (!reg) return null;
    const ip = reg.investor_profile;
    if (!ip) return null;
    if (ip.not_declared === true || ip.profile == null) {
      return {
        profile: null,
        not_declared: true,
        days_until_expiration: null,
        expired: false,
      };
    }
    const days = ip.days_to_expire != null ? ip.days_to_expire : null;
    const expired = ip.is_expired === true || (days != null && days < 0);
    return {
      profile: ip.profile,
      not_declared: false,
      days_until_expiration: days,
      expired,
    };
  }

  getShareholderProfileConfig(
    profileNumber: number | null,
  ): { type: string; badgeClass: string } | null {
    const configs: { [k: number]: any } = {
      1: { type: 'Conservador', badgeClass: 'profile-badge-conservador' },
      2: { type: 'Moderado', badgeClass: 'profile-badge-moderado' },
      3: { type: 'Agressivo', badgeClass: 'profile-badge-agressivo' },
    };
    return configs[profileNumber || 0] || null;
  }

  getShareholderExpirationText(row: any): string {
    const data = this.getShareholderInvestorProfileData(row);
    if (!data || data.not_declared || data.profile === null) return '';
    const days = data.days_until_expiration ?? 0;
    const expired = data.expired || days < 0;
    if (expired) return `Expirado há ${Math.abs(days)} dias`;
    return `Expira em ${days} dias`;
  }

  constructor(
    public fundService: FundService,
    private maskPipe: MaskPipe,
    private router: Router,
    private api: ApiService,
    private http: HttpClient,
    private route: ActivatedRoute,
    private toast: ToastService,
    private authService: AuthService,
    private formBuilder: FormBuilder,
  ) {
    this.filterForm = this.formBuilder.group({
      name: [null],
      document: [null],
      statusHomologado: [false],
      statusEmAprovacao: [false],
      statusRejeitado: [false],
      statusEmEdicao: [false],
      profileConservador: [false],
      profileModerado: [false],
      profileAgressivo: [false],
      exportIncludeEmail: [false],
      exportIncludePhone: [false],
    });
  }

  ngOnInit(): void {
    this.canView = this.authService.verifyPermission(
      'de_can_view_fund_shareholder_details',
    );

    // `[]` é truthy em JS — não reutilizar cache vazio senão a API nunca roda de novo.
    const cached = this.fundService.fundData?.shareholders;
    if (Array.isArray(cached) && cached.length > 0) {
      this.data = cached;
      this.loading = false;
    } else {
      this.getFundData(null);
    }
  }

  /** Colunas da grade (Cotistas + Status + Perfil + ação). E-mail/telefone só na exportação. */
  get shareholderTableGridStyle(): Record<string, string> {
    const parts = ['2fr', '1fr', '1fr'];
    if (this.canView) parts.push('minmax(52px, auto)');
    return { 'grid-template-columns': parts.join(' ') };
  }

  onShareholderViewClick(row: any): void {
    const reg = row?.links?.register;
    if (!this.canView || !reg?.id || reg.approval_status === 'em_edicao') return;
    this.handleRedirect(row);
  }

  refreshData(): void {
    this.getFundData(this.buildApiFilters());
  }

  toggleExportOptions(): void {
    this.showExportOptions = !this.showExportOptions;
  }

  toggleFilterModal(): void {
    this.showFilterModal = !this.showFilterModal;
  }

  /** Monta o objeto de filtros para a API a partir do formulário. */
  private buildApiFilters(): {
    name?: string;
    cpf?: string;
    cnpj?: string;
    status?: string[];
    profile?: number[];
  } | null {
    const v = this.filterForm?.value ?? {};
    const name = (v.name || '').trim() || undefined;
    const docOnly = Utils.onlyNumbers(v.document || '');
    const statuses: string[] = [];
    if (v.statusHomologado) statuses.push('aprovado');
    if (v.statusEmAprovacao) statuses.push('em_aprovacao');
    if (v.statusRejeitado) statuses.push('rejeitado');
    if (v.statusEmEdicao) statuses.push('em_edicao');
    const profiles: number[] = [];
    if (v.profileConservador) profiles.push(1);
    if (v.profileModerado) profiles.push(2);
    if (v.profileAgressivo) profiles.push(3);

    const cpf = docOnly.length === 11 ? docOnly : undefined;
    const cnpj = docOnly.length === 14 ? docOnly : undefined;

    if (
      !name &&
      !cpf &&
      !cnpj &&
      statuses.length === 0 &&
      profiles.length === 0
    )
      return null;
    return {
      ...(name && { name }),
      ...(cpf && { cpf }),
      ...(cnpj && { cnpj }),
      ...(statuses.length && { status: statuses }),
      ...(profiles.length && { profile: profiles }),
    };
  }

  async applyFilters(): Promise<void> {
    this.hasFilters = this.canApplyFilters;
    this.generateFilterTags(this.filterForm.value);
    const success = await this.getFundData(this.buildApiFilters());
    if (success) {
      this.showFilterModal = false;
    }
  }

  resetFilters(): void {
    this.filterForm.patchValue({
      name: null,
      document: null,
      statusHomologado: false,
      statusEmAprovacao: false,
      statusRejeitado: false,
      statusEmEdicao: false,
      profileConservador: false,
      profileModerado: false,
      profileAgressivo: false,
      exportIncludeEmail: false,
      exportIncludePhone: false,
    });
    this.hasFilters = false;
    this.appliedFilters = [];
    this.getFundData(null);
  }

  /** Gera as tags de filtros aplicados para exibição abaixo dos botões. */
  private generateFilterTags(formValues: any): void {
    this.appliedFilters = [];
    if (!formValues) return;
    const v = formValues;

    if (v.name?.trim()) {
      this.appliedFilters.push({
        label: `Nome / Razão Social: ${v.name.trim()}`,
        value: v.name.trim(),
      });
    }
    if (v.document?.trim()) {
      this.appliedFilters.push({
        label: `CPF / CNPJ: ${v.document.trim()}`,
        value: v.document.trim(),
      });
    }
    const statusLabels: string[] = [];
    if (v.statusHomologado) statusLabels.push('Homologado');
    if (v.statusEmAprovacao) statusLabels.push('Em aprovação');
    if (v.statusRejeitado) statusLabels.push('Rejeitado');
    if (v.statusEmEdicao) statusLabels.push('Em edição');
    if (statusLabels.length) {
      this.appliedFilters.push({
        label: `Status: ${statusLabels.join(', ')}`,
        value: statusLabels.join(', '),
      });
    }
    const profileLabels: string[] = [];
    if (v.profileConservador) profileLabels.push('Conservador');
    if (v.profileModerado) profileLabels.push('Moderado');
    if (v.profileAgressivo) profileLabels.push('Agressivo');
    if (profileLabels.length) {
      this.appliedFilters.push({
        label: `Perfil: ${profileLabels.join(', ')}`,
        value: profileLabels.join(', '),
      });
    }
    const includeParts: string[] = [];
    if (v.exportIncludeEmail) includeParts.push('E-mail');
    if (v.exportIncludePhone) includeParts.push('Telefone');
    if (includeParts.length) {
      this.appliedFilters.push({
        label: `Incluído na exportação: ${includeParts.join(', ')}`,
        value: includeParts.join(', '),
      });
    }
  }

  downloadTable(format: 'csv' | 'pdf' | 'xlsx'): void {
    this.toggleExportOptions();
    const fundId = this.route.snapshot.params.id;
    if (!fundId) {
      this.toast.show('warning', 'Aviso', 'Fundo não identificado.', null);
      return;
    }

    this.exporting = true;
    const filters = this.buildApiFilters();
    const params: Record<string, string> = {
      fund: String(fundId),
      tab: 'shareholder',
      export: format,
    };
    if (filters) {
      if (filters.name) params.name = filters.name;
      if (filters.cpf) params.cpf = filters.cpf;
      if (filters.cnpj) params.cnpj = filters.cnpj;
      if (filters.status?.length) params.status = filters.status.join(',');
      if (filters.profile?.length)
        params.profile = filters.profile.map(String).join(',');
    }
    if (this.filterForm?.get('exportIncludeEmail')?.value)
      params.include_email = '1';
    if (this.filterForm?.get('exportIncludePhone')?.value)
      params.include_telefone = '1';

    const headers = this.api.getHeaders(true, true);
    const httpParams = new HttpParams({ fromObject: params });

    this.http
      .get(`${this.api.url}/api/v2/approvals/fund-current-data/export/`, {
        headers,
        params: httpParams,
        responseType: 'blob',
        observe: 'response',
      })
      .subscribe(
        (response: any) => {
          try {
            const blob = new Blob([response.body], {
              type:
                format === 'pdf'
                  ? 'application/pdf'
                  : format === 'csv'
                    ? 'text/csv'
                    : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const contentDisposition = response.headers.get(
              'content-disposition',
            );
            let fileName = `cotistas_${new Date().toISOString().slice(0, 10)}.${format}`;
            if (contentDisposition) {
              const fileNameMatch =
                contentDisposition.match(/filename="?(.+)"?/);
              if (fileNameMatch?.[1]) fileName = fileNameMatch[1];
            }
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            this.toast.show(
              'info',
              'Sucesso',
              'Relatório gerado com sucesso!',
              null,
            );
          } catch (error) {
            console.error('Erro ao baixar exportação:', error);
            this.toast.show(
              'error',
              'Erro',
              'Falha ao baixar o arquivo.',
              null,
            );
          } finally {
            this.exporting = false;
          }
        },
        (error) => {
          console.error(error);
          this.toast.show('error', 'Erro', 'Erro ao baixar o arquivo.', null);
          this.exporting = false;
        },
      );
  }

  handleRedirect(shareholder: any) {
    this.router.navigateByUrl(
      '/app/details/shareholder/' + shareholder.links.register.id,
    );
  }

  formatDate(date: string) {
    return date.split('T')[0].split('-').reverse().join('/');
  }

  /** Formata telefone/celular para exibição: (XX) XXXX-XXXX ou (XX) 9XXXX-XXXX. */
  formatPhoneDisplay(raw: string | null | undefined): string {
    if (raw == null || String(raw).trim() === '') return '-';
    const only = String(raw).replace(/\D/g, '');
    if (only.length === 0) return '-';
    let ddd: string;
    let num: string;
    if (only.length === 10) {
      ddd = only.slice(0, 2);
      num = only.slice(2);
      return `(${ddd}) ${num.slice(0, 4)}-${num.slice(4)}`;
    }
    if (only.length === 11) {
      ddd = only.slice(0, 2);
      num = only.slice(2);
      return `(${ddd}) ${num.slice(0, 5)}-${num.slice(5)}`;
    }
    if (only.length === 12) {
      ddd = only.slice(2, 4);
      num = only.slice(4);
      return `(${ddd}) ${num.slice(0, 4)}-${num.slice(4)}`;
    }
    if (only.length === 13) {
      ddd = only.slice(2, 4);
      num = only.slice(4);
      return `(${ddd}) ${num.slice(0, 5)}-${num.slice(5)}`;
    }
    return raw;
  }

  formatDocument(user: any) {
    if (user.type === 'PF') {
      return this.maskPipe.transform(
        user.person.document.number,
        '000.000.000-00',
      );
    } else {
      return Utils.formatDocumentForDisplay(user.company.document.number);
    }
  }

  /** E-mail do cotista (PF: person.email, PJ: company.contact_email). */
  getShareholderEmail(row: any): string {
    const reg = row?.links?.register;
    if (!reg) return '';
    if (reg.type === 'PF') return reg.person?.email ?? '';
    return reg.company?.contact_email ?? '';
  }

  /** Telefone do cotista (PF: person.phone, PJ: company.phones). */
  getShareholderPhone(row: any): string {
    const reg = row?.links?.register;
    if (!reg) return '';
    if (reg.type === 'PF') {
      const p = reg.person?.phone;
      if (!p) return '';
      const ddi = p.celphone_ddi || p.telephone_ddi || '';
      const num = p.celphone_number || p.telephone_number || p.number || '';
      return [ddi, num].filter(Boolean).join(' ').trim();
    }
    const ph = reg.company?.phones;
    if (!ph) return '';
    const ddi = ph.celphone_ddi || ph.telephone_ddi || '';
    const num = ph.celphone_number || ph.telephone_number || '';
    return [ddi, num].filter(Boolean).join(' ').trim();
  }

  getInitials(user: any) {
    if (!user) return '?';
    if (user.type === 'PF') {
      const full_name = user.person.full_name;

      return Utils.getInitials(full_name);
    } else {
      const corporate_name = user.company?.corporate_name ?? '';

      return Utils.getInitials(corporate_name);
    }
  }

  /**
   * Desembrulha o corpo real de fund-current-data (array na raiz, ou `{ data: ... }`).
   */
  private unwrapFundCurrentDataResponse(raw: any): any {
    if (raw == null) return raw;
    if (Array.isArray(raw)) {
      return raw.length ? raw[0] : raw;
    }
    const inner = raw?.data;
    if (
      inner != null &&
      (inner.fund != null || inner.links != null || Array.isArray(inner))
    ) {
      return Array.isArray(inner) ? (inner.length ? inner[0] : inner) : inner;
    }
    return raw;
  }

  /** Garante o formato esperado pelo restante da tela: `{ links: { register } }`. */
  private normalizeShareholderLinkItem(item: any): any | null {
    if (item == null) return null;
    if (item.links?.register != null) return item;
    if (item.register != null) return { links: { register: item.register } };
    return null;
  }

  /**
   * Normaliza a resposta de fund-current-data: a API pode enviar cotistas em
   * `fund[].links[]` (cada item com `links.register`) ou no formato legado `links[]`.
   */
  private extractShareholderRowsFromFundCurrentData(data: {
    links?: any[];
    fund?: any;
  }): any[] {
    const rows: any[] = [];
    const fundsRaw = data?.fund;
    const funds = Array.isArray(fundsRaw)
      ? fundsRaw
      : fundsRaw != null
        ? [fundsRaw]
        : [];

    for (const f of funds) {
      const linkList = f?.links;
      if (!Array.isArray(linkList)) continue;
      for (const item of linkList) {
        const row = this.normalizeShareholderLinkItem(item);
        if (row) rows.push(row);
      }
    }
    if (rows.length > 0) return rows;
    const legacy = data?.links;
    if (!Array.isArray(legacy)) return [];
    for (const item of legacy) {
      const row = this.normalizeShareholderLinkItem(item);
      if (row) rows.push(row);
    }
    return rows;
  }

  /**
   * Carrega cotistas da API. Filtros opcionais: name, cpf, cnpj, status[], profile[].
   * status: aprovado | em_aprovacao | rejeitado | em_edicao
   * profile: 1=Conservador, 2=Moderado, 3=Agressivo
   * @returns true se a chamada teve sucesso, false em caso de erro.
   */
  async getFundData(
    filters?: {
      name?: string;
      cpf?: string;
      cnpj?: string;
      status?: string[];
      profile?: number[];
    } | null,
  ): Promise<boolean> {
    this.loading = true;
    try {
      const fundId = this.route.snapshot.params.id;
      const params: any = {
        fund: fundId,
        tab: 'shareholder',
      };
      if (filters) {
        if (filters.name) params.name = filters.name;
        if (filters.cpf) params.cpf = filters.cpf;
        if (filters.cnpj) params.cnpj = filters.cnpj;
        if (filters.status?.length) params.status = filters.status.join(',');
        if (filters.profile?.length) params.profile = filters.profile.join(',');
      }
      const raw = await this.api.get<any>({
        route: 'api/v2/approvals/fund-current-data/',
        params,
        token: true,
      });

      const data = this.unwrapFundCurrentDataResponse(raw);
      const links = this.extractShareholderRowsFromFundCurrentData(data);
      this.data = links;
      this.fundService.fundData = {
        ...this.fundService.fundData,
        shareholders: links,
      };

      this.loading = false;
      return true;
    } catch (error) {
      this.toast.show(
        'error',
        'Erro',
        'Erro ao carregar cotistas: ' + (error as any)?.message,
      );
      this.loading = false;
      return false;
    }
  }
}
